import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import axios from "axios";
import KPIBubbles from "../components/KPIBubbles";
import DataVisualizer from "../components/DataVisualizer";
import CreativeGallery from "../components/CreativeGallery";
import SmsEmailDashboard from "../components/SmsEmailDashboard";
const SharedContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 30px 20px;
`;

const Header = styled.header`
  margin-bottom: 30px;
`;

const Title = styled.h1`
  font-size: 28px;
  color: #333;
  margin-bottom: 10px;
`;

const SharedBanner = styled.div`
  background-color: #f8f9fa;
  border-left: 4px solid #6ed216;
  padding: 15px;
  margin-bottom: 20px;
  border-radius: 0 5px 5px 0;
  font-size: 14px;
  color: #666;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  font-size: 18px;
  color: #666;
`;

const ErrorContainer = styled.div`
  background-color: #ffebee;
  color: #c62828;
  padding: 15px;
  border-radius: 5px;
  text-align: center;
  margin-bottom: 20px;
`;

// Ajout d'un style pour le badge de filtre
const FilterBadge = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: #e8f0fe;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 14px;
  color: #1a73e8;
  margin-top: 10px;
`;

// Ajouter un badge pour afficher le taux de conversion
const ConversionRateBadge = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: #fef7e0;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 14px;
  color: #b06000;
  margin-top: 10px;
  margin-left: 10px;
`;

function SharedReportPage() {
  const { token } = useParams();
  const [reportData, setReportData] = useState(null);
  const [processedData, setProcessedData] = useState([]);
  const [selectedKPI, setSelectedKPI] = useState("impressions");
  const [selectedFilter, setSelectedFilter] = useState("day");
  const [insertionOrderId, setInsertionOrderId] = useState("all"); // Ajout de l'état pour stocker l'insertionOrderId
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Ajouter un état pour le taux de conversion
  const [conversionRate, setConversionRate] = useState(1);

  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";

  useEffect(() => {
    const fetchSharedReport = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${apiUrl}/api/share/${token}`);

        if (response.data.success) {
          const report = response.data.report;

          // Récupérer l'insertionOrderId et le taux de conversion
          if (response.data.insertionOrderId) {
            setInsertionOrderId(response.data.insertionOrderId);
          }

          if (response.data.conversionRate) {
            setConversionRate(response.data.conversionRate);
          }

          // Vérifier la structure des données
          if (report.data && report.data.length > 0) {
            // Traiter les données pour les rendre compatibles avec les composants
            const processed = report.data.map((item) => {
              // Extraire les valeurs de base avec des valeurs par défaut
              const impressions = Number(item.impressions || item.imps || 0);
              const clicks = Number(item.clicks || 0);
              const viewableImpressions = Number(item.viewableImpressions || 0);
              const budget = Number(item.budget || item.cost || 0);

              // Appliquer le taux de conversion au budget
              const convertedBudget = budget * conversionRate;

              // Calculer le CTR directement
              const calculatedCTR =
                impressions > 0 ? (clicks / impressions) * 100 : 0;

              // Calculer la visibilité
              let calculatedViewability = 0;
              if (impressions > 0 && viewableImpressions > 0) {
                calculatedViewability =
                  (viewableImpressions / impressions) * 100;
              }

              const processedItem = {
                date: item.date,
                creativeId: item.creativeId || item.creative_id || "",
                creativeName: item.creativeName || item.creative_name || "",
                creative:
                  item.creativeName ||
                  item.creative_name ||
                  `Creative ${
                    item.creativeId || item.creative_id || "Unknown"
                  }`,
                size: item.size || "",
                impressions: impressions,
                clicks: clicks,
                ctr: calculatedCTR,
                conversions: Number(item.conversions || item.total_convs || 0),
                budget: convertedBudget, // Utiliser le budget converti
                originalBudget: budget, // Conserver le budget original pour référence
                viewableImpressions: viewableImpressions,
                measurableImpressions: Number(item.measurableImpressions || 0),
                viewability: calculatedViewability,
                insertionOrderId: item.insertionOrderId || null,
                insertionOrderName: item.insertionOrderName || "Inconnu",
              };
              return processedItem;
            });
            setProcessedData(processed);
          } else {
            console.warn(
              "Aucune donnée dans le rapport ou structure incorrecte"
            );
          }

          setReportData(report);
        } else {
          setError("Erreur lors de la récupération des données du rapport");
        }
      } catch (err) {
        setError(`Erreur: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchSharedReport();
  }, [token, apiUrl]);

  // Filtrer les données en fonction de l'insertionOrderId
  const filteredData = useMemo(() => {
    if (!processedData || processedData.length === 0) return [];

    if (insertionOrderId === "all") {
      return processedData;
    }

    return processedData.filter(
      (item) => item.insertionOrderId === insertionOrderId
    );
  }, [processedData, insertionOrderId]);

  // Extraire les insertion orders uniques pour l'affichage du nom
  const insertionOrders = useMemo(() => {
    if (!processedData || processedData.length === 0) return [];

    const uniqueOrders = [
      ...new Set(
        processedData
          .filter((item) => item.insertionOrderId && item.insertionOrderName)
          .map((item) =>
            JSON.stringify({
              id: item.insertionOrderId,
              name: item.insertionOrderName,
            })
          )
      ),
    ].map((item) => JSON.parse(item));

    return uniqueOrders;
  }, [processedData]);

  // Obtenir le nom de l'insertion order pour l'affichage
  const getInsertionOrderName = (id) => {
    const order = insertionOrders.find((order) => order.id === id);
    return order ? order.name : "Inconnu";
  };

  const handleKPISelect = (kpi) => {
    setSelectedKPI(kpi);
  };

  return (
    <SharedContainer>
      {error && <ErrorContainer>{error}</ErrorContainer>}

      {loading ? (
        <LoadingContainer>
          Chargement des données du rapport...
        </LoadingContainer>
      ) : reportData ? (
        <>
          <Header>
            <Title>{reportData.campaignName || "Rapport de campagne"}</Title>
            <SharedBanner>
              Ce rapport a été partagé avec vous. Vous visualisez une version en
              lecture seule.
              {insertionOrderId !== "all" && (
                <FilterBadge>
                  Filtré par Insertion Order:{" "}
                  {getInsertionOrderName(insertionOrderId)}
                </FilterBadge>
              )}
              {conversionRate !== 1 && (
                <ConversionRateBadge>
                  Taux de conversion appliqué: {conversionRate > 1 ? "+" : ""}
                  {((conversionRate - 1) * 100).toFixed(2)}%
                </ConversionRateBadge>
              )}
            </SharedBanner>
          </Header>
          {reportData?.source === "sms-email" && (
            <SmsEmailDashboard reportData={reportData} />
          )}
          {filteredData.length > 0 && reportData?.source !== "sms-email" ? (
            <>
              <KPIBubbles
                data={filteredData}
                onKPISelect={handleKPISelect}
                selectedKPI={selectedKPI}
              />
              <DataVisualizer
                data={filteredData}
                selectedKPI={selectedKPI}
                selectedFilter={selectedFilter || "day"}
                onFilterChange={(filter) => setSelectedFilter(filter)}
              />
              <CreativeGallery data={filteredData} selectedKPI={selectedKPI} />
            </>
          ) : (
            <ErrorContainer>
              Aucune donnée disponible dans ce rapport
            </ErrorContainer>
          )}
        </>
      ) : (
        <ErrorContainer>Rapport non trouvé ou lien expiré</ErrorContainer>
      )}
    </SharedContainer>
  );
}

export default SharedReportPage;
